import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { delayedRetry } from '../../../../projects/platform-manager/src/lib/services/DEFAULT_MAX_RETRIES';
import { TranslateService } from '@ngx-translate/core';
import { Shop } from '../../shared/entities/shop';
import { TownInterface } from '../../shared/interfaces/town-interface';
import { OfferInterface } from '../../shared/interfaces/offer-interface';
import { OfferResponseInterface } from '../../shared/interfaces/offer-response-interface';
import { SkihirePoiResponseInterface } from '../../shared/interfaces/skihire-poi-response-interface';
import {
  BookingParametersInterface,
  codetype,
  ActioncodeInterface,
  copyBookingParameters
} from '../../shared/entities/bookingParameters';
import { SurrogateKeyService } from './surrogate-key.service';
import { RatesService } from './rates.service';
import { PartnerInterface } from '../../shared/components/header/interfaces/partner-interface';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected headers = { Accept: 'application/json' };
  protected noCacheHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-control': 'no-cache'
  };

  constructor(
    private client: HttpClient,
    private platform: PlatformInformationService,
    private translateService: TranslateService,
    private surrogateKeyService: SurrogateKeyService,
    private ratesService: RatesService
  ) {}

  /**
   * returns an Observable Array of ShopListInterface. (Shops)
   * @param bookingParameters
   */
  getLocationsBySlug(bookingParameters: BookingParametersInterface): Observable<Array<Shop>> {
    const url = this.platform
      .getApiEndpointFor(bookingParameters.partner ? 'getPartnerShops' : 'getShops')
      .replace('%partner%', bookingParameters.partner ?? '')
      .replace('%slug%', bookingParameters.slug.split('/').slice(0, 3).join('/'))
      .replace('%lang%', this.translateService.currentLang)
      .replace('%currency%', 'EUR'); //currency)
    let params: { includeNearby: number };
    if (bookingParameters.poi) {
      if (bookingParameters.poi.type === 'PointOfInterest') {
        params = { includeNearby: 0 };
      }
    }

    return this.client
      .get<Array<Shop>>(url, {
        headers: this.headers,
        observe: 'response',
        params: params
      })
      .pipe(
        map(response => {
          if (!this.platform.getIsBrowser() && response.status === 200) {
            const ids = [];
            response.body.forEach(shop => {
              if (shop.shopId) {
                ids.push(`shop-${shop.shopId}`);
              }
              // since virtual shop data is taken from parent rather often,
              // we need to reflect changes to those as well
              if (shop.virtualShopParentId) {
                ids.push(`shop-${shop.virtualShopParentId}`);
              }
              if (shop.companyId) {
                ids.push(`company-${shop.companyId}`);
              }
              if (shop.townId) {
                ids.push(`town-${shop.townId}`);
              }
            });
            this.surrogateKeyService.addSurrogateKeys(ids);
          }
          return response.body;
        }),
        catchError(() => {
          return throwError(() => new Error('Could not load locations by slug: ' + url));
        })
      );
  }

  /**
   * returns an Observable of TownInterface
   * @param slug townslug like oesterreich/tirol/soelden
   */
  getTownBySlug(slug: string): Observable<TownInterface> {
    const url = this.platform
      .getApiEndpointFor('getTown')
      .replace('%slug%', slug)
      .replace('%lang%', this.translateService.currentLang);
    return this.client.get<TownInterface>(url, { headers: this.headers }).pipe(
      tap(response => {
        // cache town in transferstate for first client run.
        if (!this.platform.getIsBrowser()) {
          // response needs to be purged because old data can be in transferstate
          this.surrogateKeyService.addSurrogateKeys(['town-' + response.id]);
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return throwError(() => new Error('Could not load town by slug: ' + url));
      })
    );
  }

  /**
   * Returns an Obeservable Array of Offers.
   *
   * @param bookingParameters
   * @param currencyCode
   * @param shopIds Array of Shop Ids (most probably found in ShopListInterface)
   * @param countryCode
   * @param offerType
   * @param days
   */
  getOffers(
    bookingParameters: BookingParametersInterface,
    currencyCode: string,
    shopIds: Array<string>,
    countryCode: string,
    offerType = 'defaultProduct',
    days?: number
  ): Observable<Array<OfferInterface>> {
    const bookingParams = copyBookingParameters(bookingParameters);

    if (shopIds.length <= 0) {
      return of([]);
    }

    if (bookingParams.ages.length < 1) {
      bookingParams.ages = [36];
    }
    const ages = [];
    bookingParams.ages.forEach(age => {
      if (age) {
        ages.push(age.toString());
      }
    });
    if (!days) {
      days = bookingParams.getDurationInDays();
    }
    const url = this.platform
      .getApiEndpointFor(bookingParams.partner ? 'getPartnerOffers' : 'getOffers')
      .replace('%partner%', bookingParams.partner)
      .replace('%lang%', this.translateService.currentLang);
    // TODO: padStart(2, '0') does the trick better
    const startDate = `${bookingParameters.start.getFullYear()}-${('0' + (bookingParameters.start.getMonth() + 1)).slice(-2)}-${('0' + bookingParameters.start.getDate()).slice(-2)}`;

    let promocode;
    if (bookingParams.codes) {
      promocode = bookingParams.codes.find(code => code.type === codetype.promo);
    }
    return this.client
      .get<OfferResponseInterface>(url, {
        headers: { Accept: 'application/json', 'Cache-Control': 'max-age=0' },
        params: {
          ['currencyCode']: 'EUR', //currencyCode,
          ['startDate']: startDate,
          ['day']: days.toString(),
          ['shopIds[]']: shopIds,
          ['personAges[]']: ages,
          ['promotionCode']: promocode ? promocode.text : '',
          ['geoIpCountryCode']: countryCode,
          ['offerType']: offerType
        },
        observe: 'response'
      })
      .pipe(
        delayedRetry<HttpResponse<OfferResponseInterface>>(8000, 2),
        map(response => {
          if (response.status === 200) {
            if (response.body.offers?.length) {
              try {
                const updatedAt = Date.now() / 1000 - +response.headers.get('Age');
                response.body.offers[0].products[0].baseTurnover.currency.updatedAt = updatedAt.toString();
                response.body.offers.forEach(offer => {
                  offer.baseTurnoverAmount = {
                    amount: offer.baseTurnover,
                    currency: offer.products[0].baseTurnover.currency
                  };
                });
                this.ratesService.patchRate(response.body.offers[0].baseTurnoverAmount);
              } catch {
                console.error('could not patch offer for: ' + url);
              }
            }
          }
          return response.body.offers;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          return throwError(() => new Error('Could not offers by shop ids: ' + url));
        })
      );
  }

  /**
   * Returns array of search results of strict (sö => söll, sölden, etc.)
   * and similar
   *
   * @param {string} query
   * @param {string} culture
   * @returns {Observable<SkihirePoiResponseInterface>}
   */
  getSkihirePois(
    query: string,
    culture: string,
    refererId?: string
  ): Observable<SkihirePoiResponseInterface> {
    const url = this.platform
      .getApiEndpointFor(refererId ? 'getAutocompleteReferer' : 'getAutocomplete')
      .replace('%refererId%', refererId)
      .replace('%query%', encodeURI(query))
      .replace('%lang%', culture);
    return this.client.get<SkihirePoiResponseInterface>(url, { headers: this.headers }).pipe(
      delayedRetry(3000),
      catchError(error => {
        console.error(error);
        console.error('Could not load skihire pois data: ' + url);
        return of(null);
      })
    );
  }

  checkPromotionCode(code: string, partner?: string): Observable<ActioncodeInterface> {
    if (!partner && this.platform?.partner) {
      partner = this.platform.partner;
    }
    const url = this.platform
      .getApiEndpointFor(partner ? 'getPartnerPromoCode' : 'getPromotionCode')
      .replace('%PROMOCODE%', encodeURI(code))
      .replace('%partner%', partner)
      .replace('%lang%', this.translateService.currentLang);
    return this.client.get<ActioncodeInterface>(url, { headers: this.headers, transferCache: false }).pipe(
      // delayedRetry(3000), no retries, 404 won't be handled correctly yet
      catchError(() => {
        return throwError(() => new Error('Could not load promotion code data'));
      }),
      map(() => {
        // Info: 'notnagel' because backend support for partner based promotion codes is broken
        if (partner !== 'partner-hervis' && code.toUpperCase().includes('MASTER')) {
          return { type: codetype.promo, text: code.toUpperCase(), errorCode: 404100 };
        } else {
          return { type: codetype.promo, text: code };
        }
      })
    );
  }

  checkCouponCode(couponcode: string): Observable<ActioncodeInterface> {
    const url = this.platform.getApiEndpointFor('getCheckCouponCodeApi').replace('%code%', couponcode);
    return this.client
      .get<{ status: string; data: { euroBalanceRaw: number; validFrom: string; validTo: string } }>(url, {
        headers: this.headers,
        transferCache: false
      })
      .pipe(
        map(response => {
          if (response.status === 'ok') {
            return {
              type: codetype.coupon,
              text: couponcode,
              euroBalanceRaw: response.data.euroBalanceRaw,
              validFrom: new Date(response.data.validFrom),
              validTo: new Date(response.data.validTo)
              // TODO: ask why coupon don't have euroBasedRates
            };
          } else {
            return { type: null, text: couponcode };
          }
        })
      );
  }

  getPartnerRefererInfo(partnerId: string): Observable<PartnerInterface> {
    const url = this.platform
      .getApiEndpointFor('getPartnerRefererInfo')
      .replace('%lang%', this.translateService.currentLang)
      .replace('%identifier%', partnerId);
    return this.client.get<PartnerInterface>(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    });
  }
}
